import {
    translateParts,
    translatePartsOptional,
} from 'aos-helpers/src/helpers/translations/Translations'
import { uuid } from 'aos-helpers/src/helpers/Uuid'
import {
    DeIcingChemicalUsagePayload,
    DeIcingTaskFormPayload,
} from 'aos-services/src/services/tasks/types/payload/DeIcingTaskFormPayload'
import { DeIcingArea } from 'aos-services/src/services/tasks/types/task/DeIcingArea'
import { DeIcingCar } from 'aos-services/src/services/tasks/types/task/DeIcingCar'
import { DeIcingChemicals } from 'aos-services/src/services/tasks/types/task/DeIcingChemicals'
import {
    allSurfaceTypes,
    DeIcingSurfaceType,
    translateSurfaceType,
} from 'aos-services/src/services/tasks/types/task/DeIcingSurfaceType'
import { TaskMetadata } from 'aos-services/src/services/tasks/types/TaskMetadata'
import { Box, Dropdown, NumberInput } from 'aos-ui-universal'
import { isNull } from 'lodash'
import React, { FC } from 'react'
import { Controller, FieldErrors, useController, useFormContext, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TaskFormField } from '../TaskFormField'
import { TaskEditableListField } from './partials/TaskEditableListField'

interface DeIcingTaskFormProps {
    metadata: TaskMetadata
    isTaskTemplate: boolean
}

export const DeIcingTaskForm: FC<DeIcingTaskFormProps> = ({ metadata, isTaskTemplate }) => {
    const { t } = useTranslation()
    const { control } = useFormContext<DeIcingTaskFormPayload>()
    const titleField = useController({ name: 'title', control })
    const payload = useWatch({ control }) as DeIcingTaskFormPayload
    const alreadyUsedChemicalTypes = payload.chemicalsUsage.map(v => v.chemicals?.chemicalsType)
    const filteredChemicals = metadata.deicingChemicals.filter(
        v => !alreadyUsedChemicalTypes.includes(v.chemicalsType),
    )
    return (
        <>
            <Box className='shrink-0 flex-row items-start'>
                <Box className='shrink-0 flex-[3]'>
                    <Controller
                        control={control}
                        name='surfaceType'
                        render={({ fieldState, field }) => (
                            <TaskFormField
                                className='mb-3'
                                label={translateParts(t)(
                                    'tasks.form.deicing.surface-type',
                                    'label',
                                )}
                                required={!isTaskTemplate}
                                error={
                                    fieldState.error
                                        ? translatePartsOptional(t)(
                                              'tasks.form.deicing.surface-type',
                                              'error',
                                          )
                                        : undefined
                                }
                                seleniumLocation='surface-type'
                            >
                                <Dropdown<DeIcingSurfaceType>
                                    items={allSurfaceTypes}
                                    value={field.value ?? null}
                                    labelRenderer={translateSurfaceType(t)}
                                    placeholder={translatePartsOptional(t)(
                                        'tasks.form.deicing.surface-type',
                                        'placeholder',
                                    )}
                                    isInvalid={!!fieldState.error}
                                    onChange={v => {
                                        field.onChange(v ?? undefined)
                                        titleField.field.onChange(
                                            v ? translateSurfaceType(t)(v) : undefined,
                                        )
                                    }}
                                />
                            </TaskFormField>
                        )}
                    />
                </Box>
                <Box className='shrink-0 flex-1 ml-5'>
                    <Controller
                        name='temperature'
                        control={control}
                        render={({ field, fieldState }) => (
                            <TaskFormField
                                className='mb-3'
                                label={translateParts(t)('tasks.form.deicing.temperature', 'label')}
                                required={!isTaskTemplate}
                                error={
                                    fieldState.error
                                        ? translatePartsOptional(t)(
                                              'tasks.form.deicing.temperature',
                                              'error',
                                          )
                                        : undefined
                                }
                                seleniumLocation='tasks.form.deicing.temperature'
                            >
                                <NumberInput
                                    value={field.value}
                                    isInvalid={!!fieldState.error}
                                    placeholder={translatePartsOptional(t)(
                                        'tasks.form.deicing.temperature',
                                        'placeholder',
                                    )}
                                    onChange={field.onChange}
                                />
                            </TaskFormField>
                        )}
                    />
                </Box>
            </Box>
            <Box className='shrink-0 flex-row items-start'>
                <Box className='shrink-0 flex-1'>
                    <Controller
                        control={control}
                        name='area'
                        render={({ field, fieldState }) => (
                            <TaskFormField
                                className='mb-3'
                                label={translateParts(t)('tasks.form.deicing.area', 'label')}
                                required={!isTaskTemplate}
                                error={
                                    fieldState.error
                                        ? translatePartsOptional(t)(
                                              'tasks.form.deicing.area',
                                              'error',
                                          )
                                        : undefined
                                }
                                seleniumLocation='area'
                            >
                                <Dropdown<DeIcingArea>
                                    items={metadata.deicingAreas}
                                    value={field.value ?? null}
                                    labelRenderer={(v: DeIcingArea) => v.label}
                                    placeholder={translatePartsOptional(t)(
                                        'tasks.form.deicing.area',
                                        'placeholder',
                                    )}
                                    isInvalid={!!fieldState.error}
                                    onChange={v => field.onChange(v ?? undefined)}
                                />
                            </TaskFormField>
                        )}
                    />
                </Box>
                <Box className='shrink-0 flex-1 ml-5'>
                    <Controller
                        control={control}
                        name='car'
                        render={({ field, fieldState }) => (
                            <TaskFormField
                                className='mb-3'
                                label={translateParts(t)('tasks.form.deicing.car', 'label')}
                                error={
                                    fieldState.error
                                        ? translatePartsOptional(t)(
                                              'tasks.form.deicing.car',
                                              'error',
                                          )
                                        : undefined
                                }
                                seleniumLocation='car'
                            >
                                <Dropdown<DeIcingCar>
                                    items={metadata.deicingCars}
                                    value={field.value ?? null}
                                    labelRenderer={(v: DeIcingCar) => v.label}
                                    placeholder={translatePartsOptional(t)(
                                        'tasks.form.deicing.car',
                                        'placeholder',
                                    )}
                                    isInvalid={!!fieldState.error}
                                    onChange={v => field.onChange(v ?? undefined)}
                                />
                            </TaskFormField>
                        )}
                    />
                </Box>
            </Box>
            <Controller
                name='chemicalsUsage'
                control={control}
                render={({ field, fieldState }) => {
                    const chemicalsErrors = (fieldState.error ??
                        []) as FieldErrors<DeIcingChemicalUsagePayload>[]
                    return (
                        <TaskEditableListField
                            keyPrefix='tasks.form.deicing.chemicals'
                            required={!isTaskTemplate}
                            className='mb-3'
                            seleniumLocation='chemicals'
                            value={field.value}
                            emptyItem={{ uuid: uuid() }}
                            onChange={chemicalsUsage =>
                                field.onChange(chemicalsUsage.length ? chemicalsUsage : [{}])
                            }
                            isError={!!fieldState.error}
                            maxItems={2}
                            itemRenderer={({ item, index, onChangeItem }) => (
                                <ChemicalsUsageRenderer
                                    allChemicals={filteredChemicals}
                                    chemicals={item}
                                    key={index}
                                    errors={chemicalsErrors?.[index]}
                                    onChange={onChangeItem}
                                    isTaskTemplate={isTaskTemplate}
                                />
                            )}
                        />
                    )
                }}
            />
        </>
    )
}
interface ChemicalsUsageRendererProps {
    chemicals: DeIcingChemicalUsagePayload
    allChemicals: DeIcingChemicals[]
    onChange(v: DeIcingChemicalUsagePayload): void
    errors: FieldErrors<DeIcingChemicalUsagePayload> | undefined
    isTaskTemplate: boolean
}

export const ChemicalsUsageRenderer: FC<ChemicalsUsageRendererProps> = ({
    chemicals,
    allChemicals,
    onChange,
    errors,
    isTaskTemplate,
}) => {
    const { t: translate } = useTranslation()

    return (
        <Box className='shrink-0 flex-row flex-wrap items-start gap-x-5 pr-4'>
            <Box className='shrink-0 basis-full grow shrink'>
                <Dropdown<DeIcingChemicals>
                    items={allChemicals}
                    value={chemicals.chemicals ?? null}
                    labelRenderer={v => v.label + ' - ' + v.chemicalsType}
                    placeholder={translate('form.deicing-usage-chemicals.placeholder', {
                        ns: 'tasks',
                    })}
                    onChange={c => onChange({ ...chemicals, chemicals: isNull(c) ? undefined : c })}
                />
            </Box>
            <Box className='shrink-0 flex-1'>
                <TaskFormField
                    label={translateParts(translate)('tasks.form.deicing-usage-amount', 'label')}
                    required={!isTaskTemplate}
                    error={
                        errors?.amount
                            ? translatePartsOptional(translate)(
                                  'tasks.form.deicing-usage-amount',
                                  'error',
                              )
                            : undefined
                    }
                >
                    <NumberInput
                        value={chemicals.amount}
                        isInvalid={!!errors?.amount}
                        max={200}
                        placeholder={translatePartsOptional(translate)(
                            'tasks.form.deicing-usage-amount',
                            'placeholder',
                        )}
                        onChange={amount => onChange({ ...chemicals, amount })}
                    />
                </TaskFormField>
            </Box>
            <Box className='shrink-0 flex-1'>
                <TaskFormField
                    label={translateParts(translate)(
                        'tasks.form.deicing-usage-total-amount',
                        'label',
                    )}
                    required={!isTaskTemplate}
                    error={
                        errors?.totalAmount
                            ? translatePartsOptional(translate)(
                                  'tasks.form.deicing-usage-total-amount',
                                  'error',
                              )
                            : undefined
                    }
                >
                    <NumberInput
                        value={chemicals.totalAmount}
                        isInvalid={!!errors?.totalAmount}
                        placeholder={translatePartsOptional(translate)(
                            'tasks.form.deicing-usage-total-amount',
                            'placeholder',
                        )}
                        onChange={amount => onChange({ ...chemicals, totalAmount: amount })}
                    />
                </TaskFormField>
            </Box>
        </Box>
    )
}
